import React, { useState } from 'react';
import {
    Box,
    Typography,
    MenuItem,
    Avatar,
    ListItemAvatar,
    Button
} from '@mui/material';
import {
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    DoneAll as DoneAllIcon,
    Handshake as HandshakeIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Autorenew as AutorenewIcon,
} from '@mui/icons-material';

// Helper local para fecha
const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function NotificationItem({ notif, onMainClick, onResolve, onMarkRead, onViewCliente, onDelete, rol }) {
    const [isOpen, setIsOpen] = useState(false);

    const isAdmin = rol === 'Admin';
    const isFuturo = notif.tipo === 'FUTURO_PROGRAMADO';
    const isConsultaConflicto = notif.tipo === 'CONSULTA_CLIENTE_CONFLICTO';
    //Agrupamos RESUELTO y AVISO_RESUELTO para que se vean iguales (Verdes)
    const isResuelto = notif.tipo === 'RESUELTO' || notif.tipo === 'AVISO_RESUELTO';

    const canReassign = isFuturo && isAdmin && notif.vendedor_activo === false;
    const canReactivate = isFuturo && notif.consulta_estado === 'FUTURO' && notif.vendedor_activo !== false;
    const actionCount = 2 + (canReassign ? 1 : 0) + (canReactivate ? 1 : 0) + (isResuelto ? 1 : 0);
    const actionsWidth = actionCount * 70;

    const handleClick = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
        if (onMainClick) onMainClick(notif);
    };

    const handleAction = (e, actionFn) => {
        e.stopPropagation();
        if (actionFn) actionFn(notif);
        setIsOpen(false);
    };

    const bgColor = notif.leido ? '#FFFFFF' : '#F0F7FF';
    const renderDescripcion = () => {
        const text = String(notif.descripcion || "");
        if (!isConsultaConflicto) return text;
        const match = text.match(/^(.*consulta\s+)(activa|futura)(.*)$/i);
        if (!match) return text;
        return (
            <>
                {match[1]}
                <Box component="span" sx={{ fontWeight: 800 }}>{match[2]}</Box>
                {match[3]}
            </>
        );
    };

    return (
        <Box sx={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid #f0f0f0', minHeight: 72 }}>
            {/* CAPA 1: BOTONES (Fondo) */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    bgcolor: '#fafafa',
                    zIndex: 1
                }}
            >
                {/* Botón LEIDO (Siempre visible al deslizar) */}
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => handleAction(e, onMarkRead)}
                    sx={{ height: '100%', borderRadius: 0, minWidth: '70px', fontSize: '0.65rem', lineHeight: 1.2, boxShadow: 'none' }}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <DoneAllIcon fontSize="small" sx={{ mb: 0.5 }} />
                        Leído
                    </Box>
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => handleAction(e, onViewCliente)}
                    sx={{ height: '100%', borderRadius: 0, minWidth: '70px', fontSize: '0.65rem', lineHeight: 1.2, boxShadow: 'none', bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <VisibilityIcon fontSize="small" sx={{ mb: 0.5 }} />
                        <Box component="span">Ver</Box>
                        <Box component="span">Cliente</Box>
                    </Box>
                </Button>
                {/* OPCIÓN A: REASIGNAR (Solo si es Futuro + Admin + vendedor inactivo) */}
                {canReassign && (
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={(e) => handleAction(e, onResolve)}
                        sx={{ height: '100%', borderRadius: 0, minWidth: '70px', fontSize: '0.65rem', lineHeight: 1.2, boxShadow: 'none' }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <HandshakeIcon fontSize="small" sx={{ mb: 0.5 }} />
                            Reasignar
                        </Box>
                    </Button>
                )}
                {canReactivate && (
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={(e) => handleAction(e, onResolve)}
                        sx={{ height: '100%', borderRadius: 0, minWidth: '70px', fontSize: '0.65rem', lineHeight: 1.2, boxShadow: 'none' }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <AutorenewIcon fontSize="small" sx={{ mb: 0.5 }} />
                            Reactivar
                        </Box>
                    </Button>
                )}
                {/* OPCIÓN B: BORRAR (Solo si es Resuelto) */}
                {isResuelto && (
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={(e) => handleAction(e, onDelete)}
                        sx={{ height: '100%', borderRadius: 0, minWidth: '70px', fontSize: '0.65rem', lineHeight: 1.2, boxShadow: 'none' }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <DeleteIcon fontSize="small" sx={{ mb: 0.5 }} />
                            Borrar
                        </Box>
                    </Button>
                )}
            </Box>
            {/* CAPA 2: CONTENIDO (Frente) */}
            <MenuItem
                onClick={handleClick}
                disableRipple
                sx={{
                    whiteSpace: 'normal',
                    alignItems: 'flex-start',
                    py: 1.5,
                    width: '100%',
                    bgcolor: bgColor,
                    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s',
                    transform: isOpen ? `translateX(-${actionsWidth}px)` : 'translateX(0)',
                    position: 'relative',
                    zIndex: 10,
                    cursor: 'pointer',
                    borderLeft: !notif.leido ? '4px solid #1976d2' : '4px solid transparent',
                    '&:hover': {
                        bgcolor: bgColor,
                        filter: 'brightness(0.97)',
                    }
                }}
            >
                <ListItemAvatar sx={{ mt: 0.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: isResuelto ? 'success.light' : 'info.main' }}>
                        {isResuelto ? <CheckCircleIcon fontSize="small" /> : <InfoIcon fontSize="small" />}
                    </Avatar>
                </ListItemAvatar>

                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: isConsultaConflicto ? 800 : (notif.leido ? 400 : 700), fontSize: '0.9rem' }}>
                            {notif.titulo}
                        </Typography>
                        {/* Indicador visual de opciones */}
                        {!isOpen && (
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', ml: 1 }}>
                                (opciones)
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.primary" sx={{ display: 'block', fontSize: '0.85rem', lineHeight: 1.3, mb: 0.5 }}>
                        {renderDescripcion()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(notif.fecha_creacion)}
                    </Typography>
                </Box>
            </MenuItem>
        </Box>
    );
}
